import { useState } from "react";

import { Button, Icon, Modal, SearchSelect, Select } from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import { clientService } from "@/services/clientService";
import { invitationService } from "@/services/invitationService";
import { useCnpjLookup, useOrganizations } from "@/hooks/useOrganizations";
import { useCreateClient } from "@/hooks/useClients";
import { TAX_REGIME_OPTIONS } from "@/types/client";
import { formatCnpj, formatCpf, stripDocument } from "@/utils/fmt";
import "../organizations/OrgCreateModal.css";

type Mode = "link" | "manual";
type DocType = "cnpj" | "cpf";

const REGIME_OPTIONS = [
  { value: "", label: "— Regime (opcional) —" },
  ...TAX_REGIME_OPTIONS.map((r) => ({ value: r, label: r })),
];

interface Row {
  id: string;
  docType: DocType;
  doc: string;
  name: string;
  regime: string;
  // Endereço do lookup (não exibido; persistido ao criar, editável no detalhe).
  address: {
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;
    zip_code: string;
  } | null;
  loading: boolean;
}

interface Props {
  workspaceId: number;
  /** Org pré-selecionada (quando aberto de dentro de uma organização). */
  organizationId?: number;
  onClose: () => void;
}

const newRow = (id: string): Row => ({
  id,
  docType: "cnpj",
  doc: "",
  name: "",
  regime: "",
  address: null,
  loading: false,
});

/** Adicionar empresas (CNPJ/CPF) a uma organização: enviar link OU cadastrar
 * manualmente (1 ou vários). Espelha o modal "Adicionar cadastros" do design. */
export function ClientCreateModal({ workspaceId, organizationId, onClose }: Props) {
  const { data: orgs = [] } = useOrganizations(workspaceId);
  const createMut = useCreateClient(workspaceId);
  const cnpjLookup = useCnpjLookup();

  const [mode, setMode] = useState<Mode>("link");
  const [orgId, setOrgId] = useState<string>(
    organizationId != null ? String(organizationId) : orgs[0] ? String(orgs[0].id) : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Link mode
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Manual mode
  const [rows, setRows] = useState<Row[]>([newRow("r1")]);
  const [createdCount, setCreatedCount] = useState<number | null>(null);

  const orgOptions = orgs.map((o) => ({
    id: String(o.id),
    label: o.nickname || o.legal_name,
    sub: o.cnpj_masked,
  }));
  const selectedOrg = orgs.find((o) => String(o.id) === orgId);

  const patch = (id: string, p: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const addRow = () => setRows((rs) => [...rs, newRow(`r${rs.length}-${rs.length + 1}`)]);
  const removeRow = (id: string) =>
    setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs));
  const setRowDoc = (id: string, docType: DocType) =>
    patch(id, { docType, doc: "", name: "" });

  const lookupRow = async (row: Row) => {
    if (row.docType !== "cnpj" || stripDocument(row.doc).length !== 14) return;
    patch(row.id, { loading: true });
    try {
      const d = await cnpjLookup.mutateAsync(row.doc);
      patch(row.id, {
        loading: false,
        name: row.name || d.razao_social || "",
        regime: row.regime || d.tax_regime || "",
        address: {
          street: d.logradouro || "",
          number: d.numero || "",
          complement: d.complemento || "",
          neighborhood: d.bairro || "",
          city: d.cidade || "",
          state: d.uf || "",
          zip_code: d.cep || "",
        },
      });
    } catch {
      patch(row.id, { loading: false });
    }
  };

  const rowValid = (r: Row) =>
    r.docType === "cnpj"
      ? stripDocument(r.doc).length === 14 && !!r.name.trim()
      : r.doc.replace(/\D/g, "").length === 11 && !!r.name.trim();
  const validRows = rows.filter(rowValid);

  const canSendLink = !!orgId && !!email.trim();
  const canCreateManual = !!orgId && validRows.length > 0;

  async function generateLink() {
    setError(null);
    setBusy(true);
    try {
      const created = await invitationService.createClient(workspaceId, {
        email: email.trim(),
        organization_id: Number(orgId),
      });
      setLink(created.accept_url);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function createManual() {
    setError(null);
    setBusy(true);
    try {
      for (const r of validRows) {
        const created = await createMut.mutateAsync({
          organization_id: Number(orgId),
          document: stripDocument(r.doc),
          legal_name: r.name.trim(),
          tax_regime: r.regime || undefined,
        });
        // Persiste o endereço do lookup (editável depois no detalhe da empresa).
        if (r.address && Object.values(r.address).some(Boolean)) {
          await clientService.setAddress(workspaceId, created.id, r.address);
        }
      }
      setCreatedCount(validRows.length);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function copyLink() {
    if (link) void navigator.clipboard?.writeText(link).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  // ── Sucesso: link ──────────────────────────────────────────────────────
  if (link) {
    return (
      <Modal
        title="Link de cadastro gerado"
        subtitle="Envie ao cliente — ele cadastra 1 ou vários CNPJs no mesmo fluxo."
        size="md"
        onClose={onClose}
        footer={<Button variant="primary" onClick={onClose}>Concluir</Button>}
      >
        <div className="orgc-link-box">
          <code>{link}</code>
          <Button variant="ghost" size="sm" iconLeft={copied ? "check" : "copy"} onClick={copyLink}>
            {copied ? "Copiado" : "Copiar"}
          </Button>
        </div>
        <p className="orgc-hint">
          Enviado a <strong>{email}</strong> · entra na organização{" "}
          <strong>{selectedOrg?.nickname || selectedOrg?.legal_name}</strong>.
        </p>
      </Modal>
    );
  }

  // ── Sucesso: manual ────────────────────────────────────────────────────
  if (createdCount != null) {
    return (
      <Modal
        title={createdCount === 1 ? "Empresa adicionada" : `${createdCount} cadastros adicionados`}
        subtitle="Próximo passo: conceder procuração e-CAC para começar a operar."
        size="sm"
        onClose={onClose}
        footer={<Button variant="primary" onClick={onClose}>Concluir</Button>}
      >
        <div className="orgc-created">
          <span className="orgc-created-icon"><Icon name="check" size={22} /></span>
          Vinculado à organização {selectedOrg?.nickname || selectedOrg?.legal_name}.
        </div>
      </Modal>
    );
  }

  // ── Formulário ──────────────────────────────────────────────────────────
  return (
    <Modal
      title="Adicionar cadastros (CNPJ/CPF)"
      subtitle="Adicione um ou vários CNPJs/CPFs a uma organização. Por link é o caminho recomendado."
      size={mode === "manual" ? "lg" : "md"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          {mode === "link" ? (
            <Button variant="primary" iconLeft="send" disabled={!canSendLink || busy} onClick={generateLink}>
              {busy ? "Gerando…" : "Gerar link"}
            </Button>
          ) : (
            <Button
              variant="primary"
              iconLeft="check"
              disabled={!canCreateManual || busy}
              onClick={createManual}
            >
              {busy
                ? "Criando…"
                : `Criar ${validRows.length || ""} ${validRows.length === 1 ? "cadastro" : "cadastros"}`.trim()}
            </Button>
          )}
        </>
      }
    >
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="orgc-modes">
        <button type="button" className={`orgc-mode ${mode === "link" ? "is-on" : ""}`} onClick={() => setMode("link")}>
          <span className="orgc-mode-icon"><Icon name="send" size={16} /></span>
          <span className="orgc-mode-tag">Recomendado</span>
          <span className="orgc-mode-title">Enviar link</span>
          <span className="orgc-mode-sub">Cliente cadastra 1 ou vários CNPJs pelo link.</span>
        </button>
        <button type="button" className={`orgc-mode ${mode === "manual" ? "is-on" : ""}`} onClick={() => setMode("manual")}>
          <span className="orgc-mode-icon"><Icon name="plus" size={16} /></span>
          <span className="orgc-mode-title">Cadastrar manualmente</span>
          <span className="orgc-mode-sub">Você adiciona os CNPJs direto (auto-preenche via Receita).</span>
        </button>
      </div>

      <div className="orgc-fields">
        <div className="form-field">
          <span className="orgc-eyebrow">Organização-dona</span>
          <SearchSelect
            value={orgId || null}
            onChange={setOrgId}
            options={orgOptions}
            placeholder="Selecione a organização…"
            emptyLabel="Nenhuma organização"
          />
        </div>

        {mode === "link" ? (
          <>
            <label className="form-field">
              <span>Enviar para</span>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@empresa.com.br"
              />
            </label>
            <label className="form-field">
              <span>Mensagem pessoal (opcional)</span>
              <textarea
                className="form-input"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Olá! Aqui está o link pra adicionar novos CNPJs do grupo no TDAX."
              />
            </label>
          </>
        ) : (
          <div className="orgc-rows">
            {rows.map((row, idx) => {
              const isPj = row.docType === "cnpj";
              return (
                <div key={row.id} className="orgc-row">
                  <div className="orgc-row-head">
                    <span className="emp-row-num">#{idx + 1}</span>
                    <div className="emp-doc-toggle" role="tablist">
                      <button
                        type="button"
                        className={`emp-doc-tab ${isPj ? "is-on" : ""}`}
                        onClick={() => setRowDoc(row.id, "cnpj")}
                      >
                        <Icon name="building" size={12} /> CNPJ
                      </button>
                      <button
                        type="button"
                        className={`emp-doc-tab ${!isPj ? "is-on" : ""}`}
                        onClick={() => setRowDoc(row.id, "cpf")}
                      >
                        <Icon name="user" size={12} /> CPF
                      </button>
                    </div>
                    {rows.length > 1 && (
                      <button className="row-act" onClick={() => removeRow(row.id)} aria-label="Remover">
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </div>
                  <div className="onb-field-grid onb-grid-2">
                    <label className="form-field">
                      <span>{isPj ? "CNPJ" : "CPF"} {row.loading && "· buscando…"}</span>
                      <input
                        className="form-input"
                        inputMode={isPj ? "text" : "numeric"}
                        value={row.doc}
                        onChange={(e) =>
                          patch(row.id, { doc: isPj ? formatCnpj(e.target.value) : formatCpf(e.target.value) })
                        }
                        onBlur={() => void lookupRow(row)}
                        placeholder={isPj ? "00.000.000/0001-00" : "000.000.000-00"}
                      />
                    </label>
                    <label className="form-field">
                      <span>{isPj ? "Razão social" : "Nome completo"}</span>
                      <input
                        className="form-input"
                        value={row.name}
                        onChange={(e) => patch(row.id, { name: e.target.value })}
                        placeholder="—"
                      />
                    </label>
                  </div>
                  <label className="form-field">
                    <span>
                      Regime tributário
                      {row.docType === "cnpj" && !!row.regime ? " · da Receita" : ""}
                    </span>
                    <Select
                      value={row.regime || ""}
                      onChange={(v) => patch(row.id, { regime: v ?? "" })}
                      options={REGIME_OPTIONS}
                      disabled={row.docType === "cnpj" && !!row.regime}
                    />
                  </label>
                </div>
              );
            })}
            <Button variant="ghost" size="sm" iconLeft="plus" onClick={addRow}>
              Adicionar outro cadastro
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
